// @flow

import type {
  EventAction,
  EventActionCreator,
  EventHandler,
  GtmGlobalEvent,
  GtmSearchEvent,
} from 'redux/middleware/googleTagManager/types';
import type {
  Journey,
  OrganizerSpecial,
  Reservation,
} from 'redux/v1/journey/models';
import type {Page} from 'types';

import {getUserIdSelector} from '@travel-frontend/user-id';

import {config} from 'config';
import {
  CUSTOMER_IS_TRAVELER_CHECKBOX,
  EXPEDIA_TYPE_GROUP_FLEX,
  INVIA_TYPE_GROUP_FLEX,
  INVIA_TYPE_GROUP_PROMOTION,
  INVIA_TYPE_GROUP_SAFETY,
  PAGE_CHECKOUT,
  TRAVEL_TYPE_OWN_ARRIVAL,
  TRAVEL_TYPE_PACKAGE_TOUR,
} from 'consts';
import {EVENT_PAGE_LOAD} from 'redux/middleware/googleTagManager/actionTypes';
import {
  getDeviceType,
  getOperatingSystem,
} from 'redux/middleware/googleTagManager/utils/environment';
import {buildVirtPath} from 'redux/middleware/googleTagManager/utils/virtPath';
import {getJourney} from 'redux/v1/journey/reducer';
import {formatShortISO, replaceEmptyStringsWithUndefined} from 'utils';

type Payload = {
  page: Page,
};

type Action = EventAction<Payload>;

type Event = {
  ...GtmGlobalEvent,
  ...GtmSearchEvent,
  virtpath: string,
  payment_option?: string,
  checkout_step?: string,
  vacancy_session_id?: string,
};

export const action: EventActionCreator<Action, Payload> = (payload) => ({
  type: EVENT_PAGE_LOAD,
  payload,
});

export const handler: EventHandler<Action, Event> = (action, state) => {
  const {page} = action.payload;
  const userId = getUserIdSelector(state);
  const journey = getJourney(state);
  const global = mapGlobalParams(userId);
  const search = mapSearchParams(journey);
  const travelType =
    journey.port === 653 ? TRAVEL_TYPE_OWN_ARRIVAL : TRAVEL_TYPE_PACKAGE_TOUR;
  const topHotels =
    config?.topHotels[
      travelType === 'packageTour'
        ? TRAVEL_TYPE_PACKAGE_TOUR
        : TRAVEL_TYPE_OWN_ARRIVAL
    ];
  const isTopHotel = topHotels.includes(journey?.giataId);
  const customerIsTravelerSessionStorage = JSON.parse(
    sessionStorage.getItem(CUSTOMER_IS_TRAVELER_CHECKBOX) || 'null'
  );
  const customerIsTraveler =
    (!customerIsTravelerSessionStorage && journey.rooms.length === 1) ||
    (customerIsTravelerSessionStorage &&
      customerIsTravelerSessionStorage.isChecked)
      ? 'on'
      : 'off';

  return replaceEmptyStringsWithUndefined({
    ...global,
    ...search,
    virtpath: buildVirtPath({
      agent: config.agent,
      page,
      travelType,
      pageLoad: action.payload.pageLoad,
    }),
    vacancy_session_id: journey.vacancySessionId,
    payment_option: serializePayments(journey.paymentMethods),
    checkout_step: page === PAGE_CHECKOUT ? '1' : undefined,
    customer_is_traveler: customerIsTraveler,
    top_hotel: isTopHotel ? '1' : '0',
  });
};

const mapGlobalParams = (userId: string): GtmGlobalEvent => {
  return {
    global_environment: process.env.STAGE || '',
    global_osname: getOperatingSystem(),
    global_devicetype: getDeviceType(),
    global_userid: userId,
  };
};

const mapSearchParams = (journey: Journey): GtmSearchEvent => {
  return {
    search_destination_id: journey.destId, // FBO-1878
    search_form: journey.port === 653 ? 'oa' : 'pt',
    search_startdate: formatShortISO(journey.departureDate),
    search_enddate: formatShortISO(journey.returnDate),
    search_duration: serializeValue(journey.duration),
    search_duration_min: serializeValue(
      journey?.originalOffer?.search?.durationMin
    ),
    search_duration_max: serializeValue(
      journey?.originalOffer?.search?.durationMax
    ),
    search_number_of_rooms: serializeValue(journey.rooms.length),
    search_number_of_adults: serializeValue(journey.adults),
    search_number_of_children: serializeValue(journey.children.length),
    search_child_ages: journey.children?.join(','),
    search_room: journey.roomType,
    search_board: journey.cateringType,
    search_baggage: serializeValue(Number(journey.baggageIncluded)),
    search_transfer: journey.transportations?.transfer || '',
    search_departure_codes:
      journey.port === 653
        ? ''
        : journey.flights?.filter((flight) => flight.outward)[0]
            .departureAirportCode,
    search_destination_codes:
      journey.port === 653
        ? ''
        : journey.flights?.filter((flight) => !flight.outward)[0]
            .departureAirportCode,
    search_seaview: journey.oceanViewType,
    search_organizer_promotion: serializeSpecials(
      journey.supplier.specialInfo.filter(
        (special) =>
          special.inviaType?.slice(0, 5) === INVIA_TYPE_GROUP_PROMOTION
      )
    ),
    search_flex_option: serializeSpecials(
      journey.supplier.specialInfo.filter(
        (special) =>
          special.inviaType?.slice(0, 5) === INVIA_TYPE_GROUP_FLEX ||
          special.inviaType?.slice(0, 4) === EXPEDIA_TYPE_GROUP_FLEX
      )
    ),
    search_safety_option: serializeSpecials(
      journey.supplier.specialInfo.filter(
        (special) => special.inviaType?.slice(0, 5) === INVIA_TYPE_GROUP_SAFETY
      )
    ),
    search_special: serializeSpecials([], journey.reservation),
    search_region: `${journey.hotel.countryCode}_${(
      journey.hotel.region ?? journey.hotel.country
    ).replaceAll(/\s+/g, '-')}`,
  };
};

const serializeValue = (value: any): string => {
  return String(value || value === 0 ? value : '');
};

const serializePayments = (paymentMethods: any[]) => {
  return paymentMethods.map((payment) => payment.reference).join(',');
};

export const serializeSpecials = (
  organizerSpecials: OrganizerSpecial[],
  reservation?: Reservation | null
): string => {
  const specials = organizerSpecials.map((special) => special.inviaType);

  if (reservation?.reservationType) {
    specials.push(`${reservation.reservation}-${reservation.reservationType}`);
  }

  return specials.join(',');
};
